import { CircularProgress, InputAdornment, TextField } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_POST, DBRequest, useConfig } from '../../../api/api';

// Text field for a manual project number. Leave empty to let the backend
// auto generate one. Non-empty values are checked (debounced) against
// `projects/check-nr` (200 = free, 422 = already in use).
// Optional field props:
// - prefixKey: dataCollection key holding the prefix (default 'prefix')
// - onCheckStateChange(blocked): reports whether submit should be blocked
//   (check pending or number already in use)
const TypeProjectNr = ({
  field,
  dataCollection,
  startDataCollection,
  updateValidationOnChange,
  onError = () => {},
  size,
}) => {
  const { t } = useTranslation();
  const config = useConfig();

  const { key, label, colspan, placeholder, disabled } = field;
  const prefixKey = field.prefixKey || 'prefix';

  const value = dataCollection[key] || '';
  const prefix = dataCollection[prefixKey] || '';

  // 'idle' | 'checking' | 'valid' | 'invalid'
  const [status, setStatus] = useState('idle');
  const requestIdRef = useRef(0);

  const [errorMessage, setErrorMessage] = useState('');

  function reportCheckState(blocked) {
    if (field.onCheckStateChange) field.onCheckStateChange(blocked);
  }

  function handleTextChange(e) {
    updateValidationOnChange([key], e.target.value);
  }

  useEffect(() => {
    // invalidate any in-flight check
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    // empty -> auto generated, nothing to check
    if (!value) {
      setStatus('idle');
      onError(null);
      reportCheckState(false);
      return;
    }

    // unchanged number of the edited project -> no check needed
    if (
      startDataCollection?.id &&
      value === (startDataCollection[key] || '') &&
      prefix === (startDataCollection[prefixKey] || '')
    ) {
      setStatus('idle');
      onError(null);
      reportCheckState(false);
      return;
    }

    setStatus('checking');
    reportCheckState(true);

    const timeout = setTimeout(() => {
      DBRequest({
        config,
        path: 'projects/check-nr',
        method: API_POST(prefix ? { nr: value, prefix } : { nr: value }),
        disableRetry: true,
        onResponse: () => {
          if (requestId !== requestIdRef.current) return;
          setStatus('valid');
          onError(null);
          reportCheckState(false);
        },
        onError: (error, errorStatus) => {
          if (requestId !== requestIdRef.current) return;
          if (errorStatus === 422) {
            // use the backend validation message (e.g. illegal characters),
            // fall back to the generic duplicate message
            const message =
              error?.errors?.nr?.[0] ||
              error?.message ||
              t('This number is already in use');
            setStatus('invalid');
            setErrorMessage(message);
            onError(message);
            reportCheckState(true);
          } else {
            // check itself failed -> do not block the user
            setStatus('idle');
            onError(null);
            reportCheckState(false);
          }
        },
      });
    }, 500);

    return () => clearTimeout(timeout);
  }, [value, prefix, config]);

  const showError = status === 'invalid';

  return (
    <TextField
      sx={{
        gridColumn: colspan ? `span ${colspan} / span ${colspan}` : 'auto',
        width: '100%',
      }}
      size={size}
      label={label}
      value={value}
      onChange={handleTextChange}
      placeholder={placeholder ?? t('Auto generated')}
      // keep the label shrunk so the placeholder is visible without focus
      InputLabelProps={{ shrink: true }}
      error={showError}
      helperText={showError ? errorMessage : undefined}
      disabled={disabled}
      inputProps={{
        autoCorrect: 'off',
        autoCapitalize: 'off',
        spellCheck: false,
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {status === 'checking' && <CircularProgress size={18} />}
            {status === 'valid' && (
              <CheckCircleIcon color="success" fontSize="small" />
            )}
            {status === 'invalid' && (
              <CancelIcon color="error" fontSize="small" />
            )}
          </InputAdornment>
        ),
      }}
    />
  );
};

export default TypeProjectNr;
